import { useRemoteClientGet, useRemoteClientPost, useRemoteClientPut } from '@/configs/http-client'
import { Endpoints } from '@/remote/endpoints'
import { useLocalStorage } from '@/remote/local-storage'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useHookFormMask } from 'use-mask-input'
import { ComboSelect } from '../components/combo-select'
import { Button, Input } from '../components/ui/components'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../components/ui/form'
import { useToast } from '../hooks'
import { states } from '../utils/states'
import { UnitsFormData, unitsSchema } from '../validators/units-validator'

interface UnitsFormProps {
  data?: UnitsFormData
  closeDialog?: () => void
  updateList?: () => void
}

export default function UnitsForm({ data, closeDialog, updateList }: UnitsFormProps) {
  const { remoteClientGet } = useRemoteClientGet()
  const { httpClientPost } = useRemoteClientPost()
  const { httpClientPut } = useRemoteClientPut()
  const { useFetchLocalStore } = useLocalStorage()
  const { showToast } = useToast()
  const isEditMode = Boolean(data?.id)

  const form = useForm<UnitsFormData>({
    resolver: zodResolver(unitsSchema),
    defaultValues: {
      id: data?.id || 0,
      nomeFantasia: data?.nomeFantasia || '',
      razaoSocial: data?.razaoSocial || '',
      cnpj: data?.cnpj || '',
      endereco: {
        estado: data?.endereco?.estado || '',
        cidade: data?.endereco?.cidade || '',
        cep: data?.endereco?.cep || '',
        rua: data?.endereco?.rua || '',
        numero: data?.endereco?.numero || '',
        complemento: data?.endereco?.complemento || '',
        bairro: data?.endereco?.bairro || ''
      },
      contato: {
        telefone: data?.contato?.telefone || '',
        email: data?.contato?.email || ''
      },
      academiaId: data?.academiaId || useFetchLocalStore().idAcademia,
      inclusao: data?.inclusao || ''
    }
  })

  const onSubmit = async (formData: UnitsFormData) => {
    const formattedData = {
      ...formData
    }

    try {
      if (isEditMode) {
        await httpClientPut(Endpoints.units, { ...formattedData })
        closeDialog?.()
        updateList?.()
      } else {
        await httpClientPost(Endpoints.units, { ...formattedData })
      }
      showToast('Requisição enviada com sucesso!', 'success')
      form.reset()
    } catch (error: any) {
      console.error('Erro no envio:', error?.response?.data?.errors)
      const errors = error?.response?.data?.errors
      if (errors) {
        for (const key in errors) {
          showToast(errors[key], 'error')
        }
      }
      showToast('Erro ao enviar requisição', 'error')
    }
  }

  const registerWithMask = useHookFormMask(form.register)

  const formValues = form.watch()

  useEffect(() => {
    if (/^\d{5}-\d{3}$/.test(formValues?.endereco?.cep.trim())) {
      const cep = formValues.endereco.cep
      remoteClientGet('/cep/' + cep)
        .then((data) => {
          const response = data.data.data
          form.setValue('endereco.bairro', response?.bairro || '')
          form.setValue('endereco.rua', response?.logradouro || '')
          form.setValue('endereco.complemento', response?.complemento || '')
          form.setValue('endereco.cidade', response?.localidade || '')
          const state = states.find((state) => state.value.endsWith(`- ${response?.uf}`))?.value || ''
          form.setValue('endereco.estado', state)
        })
        .catch((error) => {
          console.error(error)
        })
    }
  }, [formValues?.endereco?.cep])

  return (
    <Form {...form}>
      <form id="formUnits" onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-3 pl-1 pr-4">
        <FormField
          control={form.control}
          name="nomeFantasia"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                <span className="text-gray-400">*</span>
                Nome Fantasia
              </FormLabel>
              <FormControl>
                <Input {...field} placeholder="Nome Fantasia" />
              </FormControl>
              <FormMessage className="text-xs">{form.formState.errors.nomeFantasia?.message}</FormMessage>
            </FormItem>
          )}
        />
        <div className="grid grid-cols-2 gap-3">
          <FormField
            control={form.control}
            name="razaoSocial"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <span className="text-gray-400">*</span>
                  Razão Social
                </FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Razão Social" />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.razaoSocial?.message}</FormMessage>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="cnpj"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <span className="text-gray-400">*</span>CNPJ
                </FormLabel>
                <FormControl>
                  <Input {...field} {...registerWithMask('cnpj', 'cnpj')} placeholder="00.000.000/0000-00" />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.cnpj?.message}</FormMessage>
              </FormItem>
            )}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FormField
            name="endereco.estado"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <span className="text-gray-400">*</span> Estado
                </FormLabel>
                <FormControl>
                  <ComboSelect defaultValue={field.value} data={states} onChange={field.onChange} />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.endereco?.estado?.message}</FormMessage>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="endereco.cidade"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <span className="text-gray-400">*</span>Cidade
                </FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Ex: São Paulo" />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.endereco?.cidade?.message}</FormMessage>
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <FormField
            control={form.control}
            name="endereco.cep"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <span className="text-gray-400">*</span>CEP
                </FormLabel>
                <FormControl>
                  <Input {...field} {...registerWithMask('endereco.cep', '99999-999')} placeholder="00000-000" />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.endereco?.cep?.message}</FormMessage>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="endereco.rua"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <span className="text-gray-400">*</span>Rua
                </FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Nome da rua" />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.endereco?.rua?.message}</FormMessage>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="endereco.numero"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <span className="text-gray-400">*</span>Número
                </FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Número" />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.endereco?.numero?.message}</FormMessage>
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <FormField
            control={form.control}
            name="endereco.bairro"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Bairro</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Nome do bairro" />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.endereco?.bairro?.message}</FormMessage>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="endereco.complemento"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Complemento</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Complemento (opcional)" />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.endereco?.complemento?.message}</FormMessage>
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <FormField
            control={form.control}
            name="contato.telefone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <span className="text-gray-400">*</span>Telefone
                </FormLabel>
                <FormControl>
                  <Input {...field} {...registerWithMask('contato.telefone', '(99) 99999-9999')} placeholder="(00) 00000-0000" />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.contato?.telefone?.message}</FormMessage>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="contato.email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <span className="text-gray-400">*</span>E-mail
                </FormLabel>
                <FormControl>
                  <Input {...field} placeholder="email@exemplo.com" type="email" />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.contato?.email?.message}</FormMessage>
              </FormItem>
            )}
          />
        </div>

        {!isEditMode && (
          <Button type="submit" className="bg-acelerafit-green-400 text-muted  font-bold hover:bg-acelerafit-green-50 mt-3 ">
            Enviar
          </Button>
        )}
      </form>
    </Form>
  )
}
